import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Link, ButtonGroup, SubmitButton } from '@common/components';
import { toYearMonthDay } from 'works/legacy/lib/date-helper';
import { ApolloError, gql, useMutation, useNavigate } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import moment, { Moment } from 'moment';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  UploadFile,
} from 'antd';
import {
  createMessage,
  mutationResponseMessage,
} from 'works/legacy/lib/message-helper';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import {
  ShiftTypesRadioInput,
  YearsOfExperienceSelect,
} from '@jobs/components';
import { Colors, Spacing } from 'works/legacy/lib/style-helper';
import { WorksUsersSelect } from 'works/components/form-fields/works-users-select';
import { ApprovalChainConfig } from '@approvals/components/approval-chain-template-select';
import { getMessageOnStaffingRequestSuccess } from './staffing-request-form-message-helper';
import { FullValidationErrorFragmentType } from '@common/graphql/fragments';
import { BillingCardType } from '@institutions/graphql';
import { ShiftType } from '@common/graphql';

const STAFFING_REQUEST_CREATE = gql`
  mutation createStaffingRequest(
    $staffingRequestTemplateId: ID!
    $input: CreateStaffingRequestInput!
    $approvalChainTemplateSource: String
  ) {
    createStaffingRequest(
      staffingRequestTemplateId: $staffingRequestTemplateId
      input: $input
      approvalChainTemplateSource: $approvalChainTemplateSource
    ) {
      staffingRequest {
        id
        name
        approvalChains {
          id
          satisfied
          currentApprovers {
            id
            name
          }
        }
      }
      errors {
        attribute
        humanAttribute
        errors
        fullErrors
      }
    }
  }
`;

const INITIAL_SHIFT_TYPE = {
  on_demand: 'variable',
  long_term: null,
};

type WorksUser = { id: string; name: string };

type StaffingRequestTemplate = {
  id: string;
  description: string;
  yearsOfExperience?: string;
  billingRegularRate: number;
  hoursPerShift: number;
  shiftsPerWeek: number;
  staffingRequestType: 'on_demand' | 'long_term';
  externalId?: string;
  role: {
    id: string;
  };
  roleLevel: string;
  organizationUnit: OrganizationUnit;
  organization?: {
    internationalAsDefault: boolean;
  };
};

type OrganizationUnit = {
  id: string;
  department: {
    id: string;
    activeNonInternalWorksMemberships: {
      id: string;
      worksUser: {
        id: string;
        name: string;
      };
    }[];
  };
};

type StaffingRequestValues = {
  approvalDocument: {
    file: UploadFile;
  };
  approvalChainTemplateSource: string;
  hiringLeaderId: string;
  length: string;
  quantity: string;
  shiftType: string;
  startDate: Date;
  description: string;
  yearsOfExperience?: string | null;
  hoursPerShift?: string | null;
  shiftsPerWeek?: string | null;
  externalId?: string | null;
  commentContent?: string;
  international?: boolean;
};

type StaffingRequestResponse = {
  createStaffingRequest: {
    errors?: FullValidationErrorFragmentType[];
    staffingRequest?: {
      id: string;
      [key: string]: unknown;
    };
  };
};

type Props = {
  staffingRequestTemplate?: StaffingRequestTemplate;
  organizationUnitId: string;
  billingCard?: BillingCardType;
  loadBillingCard: ({
    variables,
  }: {
    variables: {
      effectiveDate: string;
      organizationUnitId: string;
      roleId?: string;
      roleLevel?: string;
    };
  }) => void;
  loadingBillingCard: boolean;
  estimatedCostParams: {
    shiftsPerWeek: number;
    hoursPerShift: number;
    lengthInWeeks: number;
    openPositionQuantity: number;
    shiftType?: ShiftType;
  };
  setEstimatedCostParams: Dispatch<
    SetStateAction<{
      shiftsPerWeek: number;
      hoursPerShift: number;
      lengthInWeeks: number;
      openPositionQuantity: number;
      shiftType?: ShiftType;
    }>
  >;
  calcEstimateCosts: () => void;
  setEstimatedCost: Dispatch<
    SetStateAction<{ lowest: number; highest: number }>
  >;
};

export function StaffingRequestForm({
  staffingRequestTemplate,
  organizationUnitId,
  billingCard,
  loadBillingCard,
  loadingBillingCard,
  estimatedCostParams,
  setEstimatedCostParams,
  calcEstimateCosts,
  setEstimatedCost,
}: Props) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const templateId = staffingRequestTemplate?.id;
  const staffingRequestType = staffingRequestTemplate?.staffingRequestType;
  const isOnDemand = staffingRequestType === 'on_demand';
  const isLongTerm = staffingRequestType === 'long_term';
  const initialShiftType =
    INITIAL_SHIFT_TYPE[staffingRequestType as 'on_demand' | 'long_term'];
  const currentMembership = useCurrentMembership();
  const approvalChainActionType = 'staffing_request_open';

  const onStaffingRequestCreateSuccess = (
    response: StaffingRequestResponse
  ) => {
    setSubmitting(false);
    if (response.createStaffingRequest.errors) {
      addErrorsToForm(form, response.createStaffingRequest.errors);
      createMessage(
        'error',
        'Failed to create Staffing Request. Please try again or contact your hiring partner.'
      );
    } else {
      const staffingRequest = response.createStaffingRequest.staffingRequest;
      const message = getMessageOnStaffingRequestSuccess(staffingRequest);

      createMessage('success', message);
      navigate(`/works/staffing-requests/requests/${staffingRequestType}`);
    }
  };

  const [staffingRequestCreate] = useMutation(STAFFING_REQUEST_CREATE);

  const onFormValuesChange = async ({ startDate }: { startDate: string }) => {
    if (isLongTerm && startDate) {
      loadBillingCard({
        variables: {
          effectiveDate: startDate,
          organizationUnitId: organizationUnitId,
          roleId: staffingRequestTemplate?.role.id,
          roleLevel: staffingRequestTemplate?.roleLevel,
        },
      });
    }

    setEstimatedCostParams({
      shiftType: form.getFieldValue('shiftType') || 'day',
      hoursPerShift: parseInt(form.getFieldValue('hoursPerShift')) || 0,
      shiftsPerWeek: parseInt(form.getFieldValue('shiftsPerWeek')) || 0,
      lengthInWeeks: parseInt(form.getFieldValue('length')) || 0,
      openPositionQuantity: parseInt(form.getFieldValue('quantity')) || 0,
    });
  };

  useEffect(() => {
    if (!staffingRequestTemplate) return;
    if (loadingBillingCard && !billingCard) return;
    if (!estimatedCostParams) {
      setEstimatedCost({ lowest: 0, highest: 0 });
      return;
    }

    calcEstimateCosts();
  }, [
    staffingRequestTemplate,
    loadingBillingCard,
    billingCard,
    estimatedCostParams,
    calcEstimateCosts,
    setEstimatedCost,
  ]);

  const departmentWorksUsers =
    staffingRequestTemplate?.organizationUnit.department.activeNonInternalWorksMemberships.map(
      (membership: { worksUser: WorksUser }) => membership.worksUser
    );

  useEffect(() => {
    if (!currentMembership) return;

    // Don't set the hiring leader to current user if they're not
    // applicable for this SR's department
    if (
      departmentWorksUsers &&
      !departmentWorksUsers
        .map((worksUser: { id: string }) => worksUser.id)
        .includes(currentMembership.worksUser.id)
    )
      return;

    form.setFieldsValue({
      hiringLeaderId: currentMembership.worksUser.id,
    });

    // @TODO: verify if departmentWorksUsers is really missed as a dependency here, since adding it might cause side effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, currentMembership]);

  const internationalAsDefault =
    staffingRequestTemplate?.organization?.internationalAsDefault;

  const initialValues = {
    approvalDocument: null,
    length: internationalAsDefault ? undefined : 13,
    shiftType: initialShiftType,
    description: staffingRequestTemplate?.description,
    yearsOfExperience: staffingRequestTemplate?.yearsOfExperience,
    hoursPerShift: isOnDemand
      ? 0
      : (staffingRequestTemplate?.hoursPerShift ?? 0),
    shiftsPerWeek: isOnDemand
      ? 0
      : (staffingRequestTemplate?.shiftsPerWeek ?? 0),
    externalId: staffingRequestTemplate?.externalId,
    international: internationalAsDefault,
  };

  const onFinish = (values: StaffingRequestValues) => {
    setSubmitting(true);

    staffingRequestCreate({
      variables: {
        staffingRequestTemplateId: staffingRequestTemplate?.id,
        approvalChainTemplateSource: values.approvalChainTemplateSource,
        input: {
          approvalDocument: values.approvalDocument?.file,
          hiringLeaderId: values.hiringLeaderId,
          length: parseInt(values.length),
          quantity: parseInt(values.quantity),
          shiftType: values.shiftType,
          startDate: toYearMonthDay(values.startDate),
          description: values.description,
          yearsOfExperience: values.yearsOfExperience,
          hoursPerShift: values.hoursPerShift,
          shiftsPerWeek: values.shiftsPerWeek,
          externalId: values.externalId || null,
          commentContent: values.commentContent,
          international: values.international,
        },
      },
    }).then((response) => {
      mutationResponseMessage({
        gqlResponse: response as {
          data?: StaffingRequestResponse;
          errors?: ApolloError[] | undefined;
        },
        onSuccess: onStaffingRequestCreateSuccess,
        afterError: () => {
          setSubmitting(false);
        },
        errorMessage:
          'Failed to create Staffing Request. Please try again or contact your hiring partner.',
      });
    });
  };

  const disabledStartDate = (startValue: Moment) => {
    return startValue.valueOf() < moment().add(-1, 'days').valueOf();
  };

  const onInternationalChange = (value: boolean) => {
    if (value === true) {
      form.setFieldsValue({ length: undefined });
    }
    form.setFieldsValue({ international: value });
  };

  return (
    <Col xl={10} lg={14} md={16} xs={24}>
      <Form
        layout="vertical"
        onValuesChange={onFormValuesChange}
        onFinish={onFinish}
        form={form}
        initialValues={initialValues}
        validateMessages={{
          number: {
            range: 'Please enter a number between ${min} and ${max}',
          },
        }}
      >
        <Row gutter={[Spacing.baseSpacing / 2, 0]} align="middle">
          <Col span={12}>
            <Form.Item
              label="Start date"
              name="startDate"
              rules={[{ required: true, message: 'Please enter a start date' }]}
            >
              <DatePicker
                placeholder="mm/dd/yyyy"
                format="MM/DD/YYYY"
                disabledDate={disabledStartDate}
                size="large"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="international" label=" ">
              <Checkbox
                defaultChecked={internationalAsDefault}
                onChange={({ target: { checked } }) =>
                  onInternationalChange(checked)
                }
              >
                International Candidate
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[Spacing.baseSpacing / 2, 0]}>
          <Col span={12}>
            <Form.Item
              label="Length in weeks"
              name="length"
              rules={[
                { required: true, message: 'Please specify length in weeks.' },
                {
                  type: 'number',
                  min: 1,
                  max: 199,
                },
              ]}
            >
              <InputNumber
                min={1}
                max={199}
                size="large"
                style={{
                  width: '100%',
                }}
                placeholder="0"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Open positions"
              name="quantity"
              rules={[
                {
                  required: true,
                  message: 'Please enter a quantity',
                },
                {
                  type: 'number',
                  min: 1,
                  max: 99,
                },
              ]}
            >
              <InputNumber
                size="large"
                style={{
                  width: '100%',
                }}
                min={0}
                max={99}
                placeholder="0"
              />
            </Form.Item>
          </Col>
        </Row>

        <ShiftTypesRadioInput />

        <Row gutter={[Spacing.baseSpacing / 2, 0]}>
          <Col span={12}>
            <Form.Item
              label="Shifts per week"
              name="shiftsPerWeek"
              rules={[
                {
                  required: !isOnDemand,
                  message: 'Please enter a number',
                },
                {
                  type: 'number',
                  min: isOnDemand ? 0 : 1,
                  max: 99,
                },
              ]}
            >
              <InputNumber
                min={isOnDemand ? 0 : 1}
                max={99}
                size="large"
                style={{
                  width: '100%',
                }}
                placeholder="0"
                disabled={isOnDemand}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Hours per shift"
              name="hoursPerShift"
              rules={[
                {
                  required: !isOnDemand,
                  message: 'Please enter a number',
                },
                {
                  type: 'number',
                  min: isOnDemand ? 0 : 1,
                  max: 99,
                },
              ]}
            >
              <InputNumber
                min={isOnDemand ? 0 : 1}
                max={99}
                size="large"
                style={{
                  width: '100%',
                }}
                placeholder="0"
                disabled={isOnDemand}
              />
            </Form.Item>
          </Col>
        </Row>

        <YearsOfExperienceSelect />

        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: 'Please enter a description' }]}
        >
          <Input.TextArea rows={4} placeholder="Add job description" />
        </Form.Item>

        <Form.Item
          label="Hiring Leader"
          name="hiringLeaderId"
          rules={[
            { required: true, message: 'Please select the hiring leader' },
          ]}
          style={{ width: '100%' }}
        >
          <WorksUsersSelect
            loading={!departmentWorksUsers}
            size="large"
            worksUsers={departmentWorksUsers}
          />
        </Form.Item>

        <Form.Item
          label="External Job ID"
          name="externalId"
          style={{ width: '100%' }}
        >
          <Input size="large" placeholder="Type" />
        </Form.Item>

        <ApprovalChainConfig
          organizationUnitId={organizationUnitId}
          actionType={approvalChainActionType}
          form={form}
        />

        <Form.Item>
          <ButtonGroup>
            <Link
              to={`/works/staffing-requests/templates/${staffingRequestType}/${templateId}`}
            >
              <Button size="large">Back</Button>
            </Link>
            <SubmitButton
              disabled={submitting}
              loading={submitting}
              size="large"
            >
              Submit Request
            </SubmitButton>
          </ButtonGroup>
        </Form.Item>
      </Form>

      <style jsx global>
        {`
          form .ant-select.select-create-staffing-form {
            width: 100%;
          }
          .ant-picker-large {
            width: 100%;
          }
          .estimated-cost {
            margin-bottom: ${Spacing.baseSpacingPx};
          }
          .estimated-cost .cost-value {
            padding-left: 0.25rem;
            color: ${Colors.grass};
          }
        `}
      </style>
    </Col>
  );
}
